import { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import moment from "moment-timezone";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { ConfirmDialog, DataTable, EmptyState, PageHeader, type DataTableColumn } from '@routes-plan/ui';
import EditPaymentModal from './EditPaymentModal';
import { Payment } from '../../../models/payment';
import { Route } from '../../../models/route';
import SelectCollectbyOffice from '../../../components/filter/SelectCollectbyOffice';
import { nowOperationalDateString, operationalTimezone } from '../../../utils/operationalDateTime';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { formatCurrency } from '../../../utils/format/currency';
import RowAction from '../components/RowAction';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { pageSx, sectionSx } from '../components/layout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const PaymentCorrections: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  setPageTitle('menu.loans.payment-corrections');
  const t = useT();
  const currentDate = nowOperationalDateString();

  const [office_id, setOffice_id] = useState('');
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [loans, setLoans] = useState([]);
  const [marcado, setMarcado] = useState('');
  const [payments, setPayments] = useState([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [offices, setOffices] = useState([]);
  const [openEditar, setOpenEditar] = useState(false);
  const [selectedPayId, setSelectedPayId] = useState('');
  const { id } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [startDate] = useState(currentDate);
  const [endDate] = useState(currentDate);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const { canUpdate, canDelete } = useResourcePermissions('/loans/payment-corrections');


  useEffect(() => {
    const fetchOffices = async () => {
      try {
        showLoading();
        const response = await axios.get('offices');
        setOffices(response.data.data);
        showAlert(t('paymentCorrections.queryOk'), 'success');
      } catch (error) {
        showAlert(t('paymentCorrections.officesLoadError'), 'error');
        console.error('Error fetching offices:', error);
      } finally {
        hideLoading();
      }
    };

    fetchOffices();
  }, []);

  useEffect(() => {
    const fetchRoutes = async () => {
      if (office_id) {
        try {
          const response = await axios.get(`offices/${office_id}/routes`);
          setOfficeRoutes(response.data);
          setSelectedCollects([]);
          setSelectAll(false);
          setLoans([]);
          setPayments([]);
        } catch (error) {
          console.error('Error fetching collects:', error);
        }
      }
    };

    fetchRoutes();
  }, [office_id]);

  useEffect(() => {
    const fetchLoans = async () => {
      if (marcado) {
        try {
          const response = await axios.get(`routes/${marcado}/loans`);
          setLoans(response.data.data);
        } catch (error) {
          console.error('Error fetching loans:', error);
        }
      }
    };

    fetchLoans();
  }, [marcado]);

  const submit = async (event?: SyntheticEvent, formData?: any) => {
    if (event) {
      event.preventDefault();
    }
    if (!office_id || !startDate || !endDate || selectedCollects.length === 0) {
      showAlert(t('paymentCorrections.missingData'), 'warning');
      return;
    }

    if (!formData) {
      if (!office_id || !startDate || !endDate || selectedCollects.length === 0) {
        showAlert(t('paymentCorrections.missingData'), 'warning');
        return;
      }
      formData = {
        endDate,
        startDate,
        routeId: selectedCollects,
      };
    }
    try {
      showLoading();
      const response = await axios.post('/payments/search', formData);
      const data = response.data.data;
      setPayments(data);
      if (data.length === 0) {
        showAlert(t('paymentCorrections.noRecordsFound'), 'info');
      } else {
        showAlert(t('paymentCorrections.searchOk'), 'success');
      }
    } catch (error) {
      showAlert(t('paymentCorrections.searchError'), 'error');
      console.error('Error fetching data:', error);
    } finally {
      hideLoading();
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = (id: string | undefined) => {
    setSelectedPayId(id ?? '');
    setModalOpen(true);
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects((prevSelectedCollects) => {
      if (prevSelectedCollects.includes(value)) {
        return prevSelectedCollects.filter((id) => id !== value);
      } else {
        setMarcado(value);
        return [...prevSelectedCollects, value];
      }
    });
  };

  const handleOpenEditar = (payId: string) => {
    const selectedPay = payments.find((payment: Payment) => payment.id === parseInt(payId, 10));
    setSelectedPayId(payId);
    setOpenEditar(true);
  };

  const handleCloseEditar = () => {
    setOpenEditar(false)
    submit(undefined, undefined);
  };

  const handleDeletePay = async (payIdValue: number | string | null | undefined) => {
    const payId = Number(payIdValue);
    if (Number.isNaN(payId)) {
      showAlert(t('paymentCorrections.invalidPaymentId'), 'warning');
      return;
    }

    setPendingDelete(payId);
  };

  const confirmDeletePay = async () => {
    const payId = pendingDelete;
    setPendingDelete(null);
    if (payId == null) return;
    try {
      showLoading();
      const { data } = await axios.post('/payments/bulk-delete', { id: payId });
      showAlert(
        data?.code === 'CORRECTION_PENDING' ? t('paymentCorrections.deletePendingApproval') : t('paymentCorrections.deletedOk'),
        data?.code === 'CORRECTION_PENDING' ? 'info' : 'success',
      );
      await submit();
    } catch (error) {
      console.error('Error eliminando abono:', error);
      showAlert(t('paymentCorrections.deleteError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = officeRoutes.map(route => route.id.toString());
      setSelectedCollects(allIds);
      setSelectAll(true);
    } else {
      setSelectedCollects([]);
      setSelectAll(false);
    }
  };

  const formatTime = (value: string | number) => {
    if (!value) return '';
    const m = moment(value);
    if (!m.isValid()) return '';
    return m.tz(operationalTimezone()).format('HH:mm:ss');
  };

  const rows = [...payments]
    .sort((a: Payment, b: Payment) => {
      const routeCompare = String(a.loan.route.name || '').localeCompare(
        String(b.loan.route.name || ''),
        'es',
        { sensitivity: 'base' }
      );
      if (routeCompare !== 0) return routeCompare;

      const aDate = a.operationDate || a.paymentDate;
      const bDate = b.operationDate || b.paymentDate;
      return moment(bDate).valueOf() - moment(aDate).valueOf();
    })
    .map((payment: Payment) => ({
      id: payment.id,
      payId: payment.id,
      routeName: payment.loan.route.name,
      clientIdNumber: payment.loan.client.idNumber,
      fullName: payment.loan.client.fullName,
      value: payment.value,
      balance: payment.balance,
      installmentValue: payment.loan.installmentValue,
      time: payment.operationDate || payment.paymentDate,
    }));

  type CorrectionRow = (typeof rows)[number];
  const columns: DataTableColumn<CorrectionRow>[] = [
    { id: 'fullName', header: t('paymentCorrections.client'), value: (row) => row.fullName },
    { id: 'routeName', header: t('common.route'), value: (row) => row.routeName },
    { id: 'clientIdNumber', header: t('common.idNumber'), value: (row) => row.clientIdNumber, hideBelow: 'md' },
    { id: 'value', header: t('paymentCorrections.payment'), align: 'right', value: (row) => Number(row.value ?? 0), render: (row) => formatCurrency(row.value) },
    { id: 'balance', header: t('paymentCorrections.balance'), align: 'right', value: (row) => Number(row.balance ?? 0), render: (row) => formatCurrency(row.balance) },
    { id: 'installmentValue', header: t('paymentCorrections.installment'), align: 'right', value: (row) => Number(row.installmentValue ?? 0), render: (row) => formatCurrency(row.installmentValue), hideBelow: 'md' },
    { id: 'time', header: t('paymentCorrections.time'), value: (row) => formatTime(String(row.time ?? '')), hideBelow: 'sm' },
  ];

  return (
    <Box sx={pageSx}>
      <PageHeader title={t('paymentCorrections.title')} description={t('paymentCorrections.description')} helpAction={<ScreenTourButton />} />
      <Paper variant="outlined" component="form" onSubmit={submit} sx={sectionSx}>
        <SelectCollectbyOffice
          office_id={office_id}
          setOffice_id={setOffice_id}
          offices={offices}
          collectsOffice={officeRoutes}
          selectedCollects={selectedCollects}
          setSelectedCollects={setSelectedCollects}
          selectAll={selectAll}
          setSelectAll={setSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          handleSelectAll={handleSelectAll}
          tourPrefix="payment-corrections"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Typography variant="body2" color="text.secondary" data-tour="payment-corrections-date">
            {t('paymentCorrections.todayOnly', { date: currentDate })}
          </Typography>
          <Button type="submit" variant="contained" startIcon={<SearchOutlined />} data-tour="payment-corrections-query">
            {t('common.search')}
          </Button>
        </Box>
      </Paper>
      <DataTable<CorrectionRow>
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        searchable
        exportFileName={t('paymentCorrections.exportFileName')}
        ariaLabel={t('paymentCorrections.title')}
        tourId="payment-corrections"
        rowActions={(row) => (
          <>
            <RowAction
              label={t('common.edit')}
              icon={<EditOutlined fontSize="small" />}
              allowed={canUpdate}
              deniedLabel={t('paymentCorrections.noPermissionEdit')}
              onClick={() => handleOpenEditar(String(row.payId ?? ''))}
            />
            <RowAction
              label={t('common.delete')}
              icon={<DeleteOutlineOutlined fontSize="small" />}
              color="error"
              allowed={canDelete}
              deniedLabel={t('paymentCorrections.noPermissionDelete')}
              onClick={() => handleDeletePay(row.payId)}
            />
          </>
        )}
        emptyState={<EmptyState dense icon={<ReceiptLongOutlined />} title={t('paymentCorrections.noRecords')} description={t('paymentCorrections.emptyHint')} />}
      />
      <ConfirmDialog
        open={pendingDelete != null}
        variant="danger"
        title={t('paymentCorrections.deleteTitle')}
        message={t('paymentCorrections.confirmDelete')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDeletePay()}
        onCancel={() => setPendingDelete(null)}
      />
      <EditPaymentModal
        isOpen={openEditar}
        onClose={handleCloseEditar}
        id={selectedPayId ?? ''}
        onButtonClick={() => {
          handleCloseEditar();
        }}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </Box>
  );
};

export default PaymentCorrections;
